import React from 'react';
import { toast } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { addDeniedData, getDeniedLists, uploadDeniedData } from '../../redux/actions/configActions';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';

import Content from '../../components/Content';
import Table, { TableButton } from '../../components/Table';
import TextInput from '../../components/TextInput';
import Pagination from '../../components/Pagination';
import Loader from '../../components/Loader';
import NetworkError from '../../components/Error';
import AddDeniedModal from './AddIOCModal';
import DeleteDeniedModal from './DeleteIOCModal';

import DeniedListsStyles from './DeniedLists.module.css';

const FRONTEND_DENIED_LISTS_ERROR_CODES = {
	INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	COMPANY_FILTER_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	ADD_ENTRY_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	UPLOAD_ENTRY_FAILED: ErrorCodes.FILE_PROCESSING_FAILED,
	DELETE_ENTRY_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	REFRESH_FAILED: ErrorCodes.API_INTERNAL_ERROR,
};

const buildDeniedListsSupportError = (errorCode, pageInfo, sourceMessage) =>
	formatFrontendSupportError({
		errorCode,
		pageInfo,
		sourceMessage,
	});

const buildEmptyDeniedListsData = () => ({
	CountriesCols: [ 'Country' ],
	CountriesRows: [],
	IPSCols: [ 'IP Address' ],
	IPSRows: [],
	PortCols: [ 'Port' ],
	PortRows: [],
	HashCols: [ 'Hash' ],
	HashRows: [],
	status: true
});

const DeniedListsScreen = () => {
	const dispatch = useDispatch();
	const userLogin = useSelector((state) => state.userLogin);

	const [ userRole, setUserRole ] = React.useState('');
	const [ userCompany, setUserCompany ] = React.useState('');
	const [ selectedCompany, setSelectedCompany ] = React.useState('ALL');
	const [ availableCompanies, setAvailableCompanies ] = React.useState([]);
	const [ addDeniedListModalStatus, setAddDeniedListModalStatus ] = React.useState({ status: false, type: '' });
	const [ deleteDeniedListModalStatus, setDeleteDeniedListModalStatus ] = React.useState({
		status: false,
		type: '',
		data: ''
	});

	const [ dataLoading, setDataLoading ] = React.useState(false);
	const [ newDeniedText, setNewDeniedText ] = React.useState({ value: '', error: '' });
	const [ newDeniedFile, setNewDeniedFile ] = React.useState({ value: null, error: '' });

	const [ deniedData, setDeniedData ] = React.useState({});
	const [ searchCountryKey, setSearchCountryKey ] = React.useState('');
	const [ searchIPKey, setSearchIPKey ] = React.useState('');
	const [ searchPortKey, setSearchPortKey ] = React.useState('');
	const [ searchHashKey, setSearchHashKey ] = React.useState('');
	const [ supportErrorMessage, setSupportErrorMessage ] = React.useState('');
	const [ copiedSupportError, setCopiedSupportError ] = React.useState(false);

	const [ CountryTotalRows, setCountryTotalRows ] = React.useState(0);
	const [ CountryRows, setCountryRows ] = React.useState([]);

	const [ IPTotalRows, setIPTotalRows ] = React.useState(0);
	const [ IPRows, setIPRows ] = React.useState([]);

	const [ PortTotalRows, setPortTotalRows ] = React.useState(0);
	const [ PortRows, setPortRows ] = React.useState([]);

	const [ HashTotalRows, setHashTotalRows ] = React.useState(0);
	const [ HashRows, setHashRows ] = React.useState([]);

	const [ pageNumberCountry, setPageNumberCountry ] = React.useState(1);
	const [ pageNumberIP, setPageNumberIP ] = React.useState(1);
	const [ pageNumberPort, setPageNumberPort ] = React.useState(1);
	const [ pageNumberHash, setPageNumberHash ] = React.useState(1);
	const pageLimit = 15;

	const setDeniedListsSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
		setSupportErrorMessage(buildDeniedListsSupportError(errorCode, pageInfo, sourceMessage));
		setCopiedSupportError(false);
	}, []);

	const clearDeniedListsSupportError = React.useCallback(() => {
		setSupportErrorMessage('');
		setCopiedSupportError(false);
	}, []);

	const handleCopySupportError = React.useCallback(async () => {
		if (!supportErrorMessage) return;
		try {
			await navigator.clipboard.writeText(supportErrorMessage);
			setCopiedSupportError(true);
			setTimeout(() => setCopiedSupportError(false), 2000);
		} catch (error) {
			setCopiedSupportError(false);
		}
	}, [ supportErrorMessage ]);

	React.useEffect(
		() => {
			if (
				userLogin &&
				userLogin.userInfo &&
				userLogin.userInfo.status === true &&
				userLogin.userInfo.session &&
				userLogin.userInfo.session.role
			) {
				setUserRole(userLogin.userInfo.session.role);
				setUserCompany(userLogin.userInfo.session.company || '');
			}
		},
		[ userLogin ]
	);

	React.useEffect(
		() => {
			const getData = async () => {
				setDataLoading(true);
				clearDeniedListsSupportError();
				try {
					console.log('[DEBUG] getData called. userCompany:', userCompany, 'selectedCompany:', selectedCompany);
					const formData = new FormData();
					if (userCompany === 'FLC' && selectedCompany !== 'ALL') {
						formData.append('company', selectedCompany);
						console.log('[DEBUG] Added company to formData:', selectedCompany);
					}
					const data = await dispatch(getDeniedLists(formData));
					console.log('[DEBUG] getDeniedLists response:', data);
					
					// Check if we received valid data
					if (data && data.status && data.CountriesRows && data.IPSRows && data.PortRows) {
						setDeniedData(data);
						clearDeniedListsSupportError();

						// The backend now tells us directly whether the caller is
						// the provider (sees every client), instead of matching a
						// company literally named 'FLC'. Keep 'FLC' as the internal
						// sentinel the gates below use so their logic is unchanged.
						if (data.isProvider && userCompany !== 'FLC') {
							setUserCompany('FLC');
						}
						if (data.isProvider || userCompany === 'FLC') {
							if (data.availableCompanies && Array.isArray(data.availableCompanies)) {
								setAvailableCompanies(data.availableCompanies);
							} else {
								setAvailableCompanies([]);
							}
						}
					} else {
						// Handle empty or invalid data without crashing
						console.error('Invalid data received from API', data);
						setDeniedData(buildEmptyDeniedListsData());
						setDeniedListsSupportError(
							FRONTEND_DENIED_LISTS_ERROR_CODES.INITIAL_LOAD_FAILED,
							'Denied Lists Page / Initial Data Load Failed',
							data?.msg || (typeof data === 'string' ? data : 'Denied lists request did not return success')
						);
					}
				} catch (error) {
					console.error('Error fetching denied lists data:', error);
					// Set default empty data structure
					setDeniedData(buildEmptyDeniedListsData());
					setDeniedListsSupportError(
						FRONTEND_DENIED_LISTS_ERROR_CODES.INITIAL_LOAD_FAILED,
						'Denied Lists Page / Initial Data Request Error',
						error?.message || String(error)
					);
				} finally {
					setDataLoading(false);
				}
			};

			getData();
		},
		[ dispatch, selectedCompany, userCompany, clearDeniedListsSupportError, setDeniedListsSupportError ]
	);

	React.useEffect(
		() => {
			if (
				deniedData &&
				Object.keys(deniedData).length > 0 &&
				deniedData.CountriesRows &&
				deniedData.CountriesRows.length > 0
			) {
				if (searchCountryKey) {
					const countryData = deniedData.CountriesRows.filter((row) =>
						row.find((el) => String(el ?? '').toLowerCase().includes(String(searchCountryKey ?? '').toLowerCase()))
					);
					setCountryTotalRows(countryData.length);
					setCountryRows(countryData.slice(0, pageLimit));
				} else {
					setCountryTotalRows(deniedData.CountriesRows.length);
					setCountryRows(deniedData.CountriesRows.slice(0, pageLimit));
				}
			} else {
				setCountryTotalRows(0);
				setCountryRows([]);
			}
		},
		[ deniedData, searchCountryKey ]
	);

	React.useEffect(
		() => {
			if (deniedData && Object.keys(deniedData).length > 0 && deniedData.IPSRows && deniedData.IPSRows.length > 0) {
				if (searchIPKey) {
					const IPRowData = deniedData.IPSRows.filter((row) =>
						row.find((el) => String(el ?? '').toLowerCase().includes(String(searchIPKey ?? '').toLowerCase()))
					);
					setIPTotalRows(IPRowData.length);
					setIPRows(IPRowData.slice(0, pageLimit));
				} else {
					setIPTotalRows(deniedData.IPSRows.length);
					setIPRows(deniedData.IPSRows.slice(0, pageLimit));
				}
			} else {
				setIPTotalRows(0);
				setIPRows([]);
			}
		},
		[ deniedData, searchIPKey ]
	);

	React.useEffect(
		() => {
			if (deniedData && Object.keys(deniedData).length > 0 && deniedData.PortRows && deniedData.PortRows.length > 0) {
				if (searchPortKey) {
					const PortRowData = deniedData.PortRows.filter((row) =>
						row.find((el) => String(el ?? '').toLowerCase().includes(String(searchPortKey ?? '').toLowerCase()))
					);
					setPortTotalRows(PortRowData.length);
					setPortRows(PortRowData.slice(0, pageLimit));
				} else {
					setPortTotalRows(deniedData.PortRows.length);
					setPortRows(deniedData.PortRows.slice(0, pageLimit));
				}
			} else {
				setPortTotalRows(0);
				setPortRows([]);
			}
		},
		[ deniedData, searchPortKey ]
	);

	React.useEffect(
		() => {
			if (deniedData && Object.keys(deniedData).length > 0 && deniedData.HashRows && deniedData.HashRows.length > 0) {
				if (searchHashKey) {
					const HashRowData = deniedData.HashRows.filter((row) =>
						row.find((el) => String(el ?? '').toLowerCase().includes(String(searchHashKey ?? '').toLowerCase()))
					);
					setHashTotalRows(HashRowData.length);
					setHashRows(HashRowData.slice(0, pageLimit));
				} else {
					setHashTotalRows(deniedData.HashRows.length);
					setHashRows(deniedData.HashRows.slice(0, pageLimit));
				}
			} else {
				setHashTotalRows(0);
				setHashRows([]);
			}
		},
		[ deniedData, searchHashKey ]
	);

	let nextCountryContentFrom = 0;
	const handleCountryPageChange = async (currentPage) => {
		setPageNumberCountry(currentPage.selected + 1);

		nextCountryContentFrom = currentPage.selected * pageLimit;
		const nextContentTo = currentPage.selected * pageLimit + pageLimit;
		setCountryRows(
			deniedData &&
				Object.keys(deniedData).length > 0 &&
				deniedData.CountriesRows.length > 0 &&
				deniedData.CountriesRows
					.filter((row) => row.find((el) => String(el ?? '').toLowerCase().includes(String(searchCountryKey ?? '').toLowerCase())))
					.slice(nextCountryContentFrom, nextContentTo)
		);
	};

	let nextIPContentFrom = 0;
	const handleIPPageChange = async (currentPage) => {
		setPageNumberIP(currentPage.selected + 1);

		nextIPContentFrom = currentPage.selected * pageLimit;
		const nextContentTo = currentPage.selected * pageLimit + pageLimit;
		setIPRows(
			deniedData &&
				Object.keys(deniedData).length > 0 &&
				deniedData.IPSRows.length > 0 &&
				deniedData.IPSRows
					.filter((row) => row.find((el) => String(el ?? '').toLowerCase().includes(String(searchIPKey ?? '').toLowerCase())))
					.slice(nextIPContentFrom, nextContentTo)
		);
	};

	let nextPortContentFrom = 0;
	const handlePortPageChange = async (currentPage) => {
		setPageNumberPort(currentPage.selected + 1);

		nextPortContentFrom = currentPage.selected * pageLimit;
		const nextContentTo = currentPage.selected * pageLimit + pageLimit;
		setPortRows(
			deniedData &&
				Object.keys(deniedData).length > 0 &&
				deniedData.PortRows.length > 0 &&
				deniedData.PortRows
					.filter((row) => row.find((el) => String(el ?? '').toLowerCase().includes(String(searchPortKey ?? '').toLowerCase())))
					.slice(nextPortContentFrom, nextContentTo)
		);
	};

	let nextHashContentFrom = 0;
	const handleHashPageChange = async (currentPage) => {
		setPageNumberHash(currentPage.selected + 1);

		nextHashContentFrom = currentPage.selected * pageLimit;
		const nextContentTo = currentPage.selected * pageLimit + pageLimit;
		setHashRows(
			deniedData &&
				Object.keys(deniedData).length > 0 &&
				deniedData.HashRows.length > 0 &&
				deniedData.HashRows
					.filter((row) => row.find((el) => String(el ?? '').toLowerCase().includes(String(searchHashKey ?? '').toLowerCase())))
					.slice(nextHashContentFrom, nextContentTo)
		);
	};

	const closeModal = () => {
		setAddDeniedListModalStatus({ status: false, type: '' });
		setNewDeniedText({ value: '', error: '' });
		setNewDeniedFile({ value: null, error: '' });
	};

	const handleAddDenied = async () => {
		if (userRole !== 'admin') return;

		if (newDeniedText.value !== '') {
			if (newDeniedText.value.trim() === '') {
				setNewDeniedText((previous) => ({ ...previous, error: 'Please enter input value' }));
				return;
			}

			try {
				const formData = new FormData();

				if (addDeniedListModalStatus.type === 'Country') {
					formData.append('btn', 'AddCountry');
					formData.append('AddCountry', newDeniedText.value);
				} else if (addDeniedListModalStatus.type === 'IP Address') {
					formData.append('btn', 'AddIP');
					formData.append('AddIP', newDeniedText.value);
				} else if (addDeniedListModalStatus.type === 'Services/Ports') {
					formData.append('btn', 'AddPort');
					formData.append('AddPort', newDeniedText.value);
				} else if (addDeniedListModalStatus.type === 'Hash') {
					formData.append('btn', 'AddHash');
					formData.append('AddHash', newDeniedText.value);
				}

				if (userCompany === 'FLC' && selectedCompany !== 'ALL') {
					formData.append('company', selectedCompany);
				}

				const data = await dispatch(addDeniedData(formData));

				if (data && data.status) {
					setDeniedData(data);
					closeModal();
					clearDeniedListsSupportError();
					toast.success(`Successfully added ${addDeniedListModalStatus.type}`);
				} else {
					const sourceMessage =
						data?.msg || (typeof data === 'string' ? data : 'Denied list add request did not return success');
					const formattedError = buildDeniedListsSupportError(
						FRONTEND_DENIED_LISTS_ERROR_CODES.ADD_ENTRY_FAILED,
						'Denied Lists Page / Add Entry Failed',
						sourceMessage
					);
					setNewDeniedText((previous) => ({ ...previous, error: formattedError }));
					toast.error(formattedError);
					setDeniedListsSupportError(
						FRONTEND_DENIED_LISTS_ERROR_CODES.ADD_ENTRY_FAILED,
						'Denied Lists Page / Add Entry Failed',
						sourceMessage
					);
				}
			} catch (error) {
				const sourceMessage = error?.message || String(error);
				const formattedError = buildDeniedListsSupportError(
					FRONTEND_DENIED_LISTS_ERROR_CODES.ADD_ENTRY_FAILED,
					'Denied Lists Page / Add Entry Request Error',
					sourceMessage
				);
				setNewDeniedText((previous) => ({ ...previous, error: formattedError }));
				toast.error(formattedError);
				setDeniedListsSupportError(
					FRONTEND_DENIED_LISTS_ERROR_CODES.ADD_ENTRY_FAILED,
					'Denied Lists Page / Add Entry Request Error',
					sourceMessage
				);
			}
			return;
		}

		if (!newDeniedFile.value) {
			setNewDeniedFile((previous) => ({ ...previous, error: 'Please select input file' }));
			return;
		}

		let uploadData;
		try {
			const formData = new FormData();

			if (addDeniedListModalStatus.type === 'Country') {
				formData.append('Countryfile', newDeniedFile.value);
				if (userCompany === 'FLC' && selectedCompany !== 'ALL') {
					formData.append('company', selectedCompany);
				}
				uploadData = await dispatch(uploadDeniedData('upload-denied-country', formData));
			} else if (addDeniedListModalStatus.type === 'IP Address') {
				formData.append('IPfile', newDeniedFile.value);
				if (userCompany === 'FLC' && selectedCompany !== 'ALL') {
					formData.append('company', selectedCompany);
				}
				uploadData = await dispatch(uploadDeniedData('upload-denied-ip', formData));
			} else if (addDeniedListModalStatus.type === 'Services/Ports') {
				formData.append('PortFile', newDeniedFile.value);
				if (userCompany === 'FLC' && selectedCompany !== 'ALL') {
					formData.append('company', selectedCompany);
				}
				uploadData = await dispatch(uploadDeniedData('upload-denied-port', formData));
			}
		} catch (error) {
			const sourceMessage = error?.message || String(error);
			const formattedError = buildDeniedListsSupportError(
				FRONTEND_DENIED_LISTS_ERROR_CODES.UPLOAD_ENTRY_FAILED,
				'Denied Lists Page / Upload Entry Request Error',
				sourceMessage
			);
			setNewDeniedFile((previous) => ({ ...previous, error: formattedError }));
			toast.error(formattedError);
			setDeniedListsSupportError(
				FRONTEND_DENIED_LISTS_ERROR_CODES.UPLOAD_ENTRY_FAILED,
				'Denied Lists Page / Upload Entry Request Error',
				sourceMessage
			);
			return;
		}

		if (!uploadData || !uploadData.status) {
			const sourceMessage =
				uploadData?.msg ||
				(typeof uploadData === 'string' ? uploadData : 'Denied list upload request did not return success');
			const formattedError = buildDeniedListsSupportError(
				FRONTEND_DENIED_LISTS_ERROR_CODES.UPLOAD_ENTRY_FAILED,
				'Denied Lists Page / Upload Entry Failed',
				sourceMessage
			);
			setNewDeniedFile((previous) => ({ ...previous, error: formattedError }));
			toast.error(formattedError);
			setDeniedListsSupportError(
				FRONTEND_DENIED_LISTS_ERROR_CODES.UPLOAD_ENTRY_FAILED,
				'Denied Lists Page / Upload Entry Failed',
				sourceMessage
			);
			return;
		}

		closeModal();
		setDataLoading(true);
		try {
			const getFormData = new FormData();
			if (userCompany === 'FLC' && selectedCompany !== 'ALL') {
				getFormData.append('company', selectedCompany);
			}
			const refreshedData = await dispatch(getDeniedLists(getFormData));
			if (refreshedData && refreshedData.status && refreshedData.CountriesRows && refreshedData.IPSRows && refreshedData.PortRows) {
				setDeniedData(refreshedData);
				clearDeniedListsSupportError();
				toast.success(uploadData.msg || `Successfully added ${addDeniedListModalStatus.type}`);
			} else {
				setDeniedData(buildEmptyDeniedListsData());
				const sourceMessage =
					refreshedData?.msg ||
					(typeof refreshedData === 'string'
						? refreshedData
						: 'Denied list refresh after upload did not return success');
				const formattedError = buildDeniedListsSupportError(
					FRONTEND_DENIED_LISTS_ERROR_CODES.REFRESH_FAILED,
					'Denied Lists Page / Refresh After Upload Failed',
					sourceMessage
				);
				toast.error(formattedError);
				setDeniedListsSupportError(
					FRONTEND_DENIED_LISTS_ERROR_CODES.REFRESH_FAILED,
					'Denied Lists Page / Refresh After Upload Failed',
					sourceMessage
				);
			}
		} catch (error) {
			const sourceMessage = error?.message || String(error);
			const formattedError = buildDeniedListsSupportError(
				FRONTEND_DENIED_LISTS_ERROR_CODES.REFRESH_FAILED,
				'Denied Lists Page / Refresh After Upload Request Error',
				sourceMessage
			);
			toast.error(formattedError);
			setDeniedListsSupportError(
				FRONTEND_DENIED_LISTS_ERROR_CODES.REFRESH_FAILED,
				'Denied Lists Page / Refresh After Upload Request Error',
				sourceMessage
			);
		} finally {
			setDataLoading(false);
		}
	};

	const closeDeleteModal = () => {
		setDeleteDeniedListModalStatus({ status: false, type: '', data: '' });
	};

	const handleDeleteDenied = async () => {
		const delData = deleteDeniedListModalStatus.data;
		
		const delValue = Array.isArray(delData) ? delData[0] : delData;
		
		if (!delValue) return;

		const formData = new FormData();
		if (deleteDeniedListModalStatus.type === 'Country') {
			formData.append('btn', 'DeleteCountry');
			formData.append('DelCountry', delValue);
		} else if (deleteDeniedListModalStatus.type === 'IP Address') {
			formData.append('btn', 'DeleteIP');
			formData.append('DelIP', delValue);
		} else if (deleteDeniedListModalStatus.type === 'Services/Ports') {
			formData.append('btn', 'DeletePort');
			formData.append('DelPort', delValue);
		} else if (deleteDeniedListModalStatus.type === 'Hash') {
			formData.append('btn', 'DeleteHash');
			formData.append('DelHash', delValue);
		}
		if (userCompany === 'FLC' && selectedCompany !== 'ALL') {
			formData.append('company', selectedCompany);
		}

		try {
			const data = await dispatch(addDeniedData(formData));
			closeDeleteModal();

			if (!data || !data.status) {
				const sourceMessage =
					data?.msg || (typeof data === 'string' ? data : 'Denied list delete request did not return success');
				const formattedError = buildDeniedListsSupportError(
					FRONTEND_DENIED_LISTS_ERROR_CODES.DELETE_ENTRY_FAILED,
					'Denied Lists Page / Delete Entry Failed',
					sourceMessage
				);
				toast.error(formattedError);
				setDeniedListsSupportError(
					FRONTEND_DENIED_LISTS_ERROR_CODES.DELETE_ENTRY_FAILED,
					'Denied Lists Page / Delete Entry Failed',
					sourceMessage
				);
				return;
			}

			toast.success(`Successfully deleted ${deleteDeniedListModalStatus.type}`);
			clearDeniedListsSupportError();

			if (deleteDeniedListModalStatus.type === 'Country') {
				setPageNumberCountry(1);
			} else if (deleteDeniedListModalStatus.type === 'IP Address') {
				setPageNumberIP(1);
			} else if (deleteDeniedListModalStatus.type === 'Hash') {
				setPageNumberHash(1);
			} else {
				setPageNumberPort(1);
			}

			setDataLoading(true);
			try {
				const refreshFormData = new FormData();
				if (userCompany === 'FLC' && selectedCompany !== 'ALL') {
					refreshFormData.append('company', selectedCompany);
				}

				const refreshedData = await dispatch(getDeniedLists(refreshFormData));
				if (refreshedData && refreshedData.status && refreshedData.CountriesRows && refreshedData.IPSRows && refreshedData.PortRows) {
					setDeniedData(refreshedData);
					clearDeniedListsSupportError();
				} else {
					setDeniedData(buildEmptyDeniedListsData());
					const sourceMessage =
						refreshedData?.msg ||
						(typeof refreshedData === 'string'
							? refreshedData
							: 'Denied list refresh after delete did not return success');
					const formattedError = buildDeniedListsSupportError(
						FRONTEND_DENIED_LISTS_ERROR_CODES.REFRESH_FAILED,
						'Denied Lists Page / Refresh After Delete Failed',
						sourceMessage
					);
					toast.error(formattedError);
					setDeniedListsSupportError(
						FRONTEND_DENIED_LISTS_ERROR_CODES.REFRESH_FAILED,
						'Denied Lists Page / Refresh After Delete Failed',
						sourceMessage
					);
				}
			} finally {
				setDataLoading(false);
			}
		} catch (error) {
			const sourceMessage = error?.message || String(error);
			const formattedError = buildDeniedListsSupportError(
				FRONTEND_DENIED_LISTS_ERROR_CODES.DELETE_ENTRY_FAILED,
				'Denied Lists Page / Delete Entry Request Error',
				sourceMessage
			);
			toast.error(formattedError);
			setDeniedListsSupportError(
				FRONTEND_DENIED_LISTS_ERROR_CODES.DELETE_ENTRY_FAILED,
				'Denied Lists Page / Delete Entry Request Error',
				sourceMessage
			);
		}
	};

	const handleCompanyChange = async (e) => {
		const newCompany = e.target.value;
		// console.log('[DEBUG] Company changed to:', newCompany);
		setSelectedCompany(newCompany);
		setPageNumberCountry(1);
		setPageNumberIP(1);
		setPageNumberPort(1);
		setPageNumberHash(1);

		// Reset search filters
		setSearchCountryKey('');
		setSearchIPKey('');
		setSearchPortKey('');
		setSearchHashKey('');
		
		// Force data refresh
		setDataLoading(true);
		const formData = new FormData();
		if (userCompany === 'FLC' && newCompany !== 'ALL') {
			formData.append('company', newCompany);
		}
		
		try {
			// Immediately fetch new data with the selected company
			const data = await dispatch(getDeniedLists(formData));
			// console.log('[DEBUG] Data refreshed with new company filter:', data);
			if (data && data.status && data.CountriesRows && data.IPSRows && data.PortRows) {
				setDeniedData(data);
				clearDeniedListsSupportError();
				return;
			}
			setDeniedData(buildEmptyDeniedListsData());
			setDeniedListsSupportError(
				FRONTEND_DENIED_LISTS_ERROR_CODES.COMPANY_FILTER_LOAD_FAILED,
				'Denied Lists Page / Company Filter Refresh Failed',
				data?.msg || (typeof data === 'string' ? data : 'Denied lists company filter refresh did not return success')
			);
		} catch (error) {
			setDeniedData(buildEmptyDeniedListsData());
			setDeniedListsSupportError(
				FRONTEND_DENIED_LISTS_ERROR_CODES.COMPANY_FILTER_LOAD_FAILED,
				'Denied Lists Page / Company Filter Refresh Request Error',
				error?.message || String(error)
			);
		} finally {
			setDataLoading(false);
		}
	};

	return (
		<Content currentMenu='configuration' headerTitle='Denied Lists'>
			{dataLoading ? (
				<Loader />
			) : deniedData && Object.keys(deniedData).length === 0 ? (
				<div>
					<NetworkError />
					{supportErrorMessage && (
						<div className={DeniedListsStyles.supportErrorCard}>
							<strong>Support Error:</strong>
							<code className={DeniedListsStyles.supportErrorCode}>{supportErrorMessage}</code>
							<button
								type='button'
								className={DeniedListsStyles.copySupportErrorButton}
								onClick={handleCopySupportError}
							>
								{copiedSupportError ? 'Copied' : 'Copy Error Details'}
							</button>
						</div>
					)}
				</div>
			) : (
				<div className={DeniedListsStyles.tables}>
					{supportErrorMessage && (
						<div className={DeniedListsStyles.supportErrorBanner}>
							<div className={DeniedListsStyles.supportErrorBannerText}>
								<strong>Support Error:</strong>
								<code className={DeniedListsStyles.supportErrorCode}>{supportErrorMessage}</code>
							</div>
							<button
								type='button'
								className={DeniedListsStyles.copySupportErrorButton}
								onClick={handleCopySupportError}
							>
								{copiedSupportError ? 'Copied' : 'Copy Error Details'}
							</button>
						</div>
					)}
					{userCompany === 'FLC' && (
						<div className={DeniedListsStyles.company_selector}>
							<label htmlFor="company-select">Filter by Company:</label>
							<select 
								id="company-select" 
								value={selectedCompany}
								onChange={handleCompanyChange}
								className={DeniedListsStyles.company_select}
							>
								{availableCompanies && availableCompanies.length > 0 ? 
									availableCompanies.map((company) => (
										<option key={company} value={company}>{company}</option>
									)) : null
								}
							</select>
							{selectedCompany === 'ALL' && userRole === 'admin' && (
								<div className={DeniedListsStyles.company_selector_note}>
									Note: You must select a specific company to add or remove items.
								</div>
							)}
						</div>
					)}
					
					<div className={DeniedListsStyles.table}>
						<div className={DeniedListsStyles.table_heading}>
							<div className={DeniedListsStyles.table_header}>
								<h3 className={DeniedListsStyles.table_header_text}>Denied Countries</h3>
								{userRole === 'admin' && (
									<button
										className={DeniedListsStyles.table_header_btn}
										onClick={() => setAddDeniedListModalStatus({ status: true, type: 'Country' })}
										data-testid='add-denied'>
										<svg className={DeniedListsStyles.table_header_btn_icon}>
											<use xlinkHref={`/assets/sprite.svg#icon-plus`} />
										</svg>
									</button>
								)}
							</div>
							<TextInput
								icon='search'
								placeholder='Search Here'
								value={searchCountryKey}
								onChange={(e) => setSearchCountryKey(e.target.value)}
								style={{ width: '15rem' }}
							/>
						</div>
						<Table
							containerStyle={{ height: '70vh' }}
							tableHead={
								<tr>
									{deniedData.CountriesCols &&
										deniedData.CountriesCols.length > 0 &&
										deniedData.CountriesCols.map((col, index) => <th key={col + index}>{col}</th>)}
								</tr>
							}
							tableRow={
								CountryTotalRows > 0 && CountryRows.length > 0 ? (
									CountryRows.map((rows, index) => (
										<tr key={index}>
											{rows &&
												rows.length > 0 &&
												rows.map((row, rowIndex) => (
													<td key={row + rowIndex}>{row}</td>
												))}
											{userRole === 'admin' && (
												<td>
													<TableButton
														label='Delete'
														icon='trash'
														onClick={() =>
															setDeleteDeniedListModalStatus({ status: true, type: 'Country', data: rows })}
													/>
												</td>
											)}
										</tr>
									))
								) : (
									<tr>
										<td
											colSpan={deniedData.CountriesCols && deniedData.CountriesCols.length}
											style={{ textAlign: 'center' }}>
											No data found
										</td>
									</tr>
								)
							}
						/>
						<div className='paginationContainer'>
							<Pagination
								onPageChange={handleCountryPageChange}
								rowsPerPage={pageLimit}
								totalPageSize={CountryTotalRows}
								pageNumber={pageNumberCountry}
							/>
						</div>
					</div>

					<div className={DeniedListsStyles.table}>
						<div className={DeniedListsStyles.table_heading}>
							<div className={DeniedListsStyles.table_header}>
								<h3 className={DeniedListsStyles.table_header_text}>Denied IPs Addresses</h3>
								{userRole === 'admin' && (
									<button
										className={DeniedListsStyles.table_header_btn}
										onClick={() => setAddDeniedListModalStatus({ status: true, type: 'IP Address' })}>
										<svg className={DeniedListsStyles.table_header_btn_icon}>
											<use xlinkHref={`/assets/sprite.svg#icon-plus`} />
										</svg>
									</button>
								)}
							</div>
							<TextInput
								icon='search'
								placeholder='Search Here'
								value={searchIPKey}
								onChange={(e) => setSearchIPKey(e.target.value)}
								style={{ width: '15rem' }}
							/>
						</div>
						<Table
							containerStyle={{ height: '70vh' }}
							tableHead={
								<tr>
									{deniedData.IPSCols &&
										deniedData.IPSCols.length > 0 &&
										deniedData.IPSCols.map((col, index) => <th key={col + index}>{col}</th>)}
								</tr>
							}
							tableRow={
								IPTotalRows > 0 && IPRows.length > 0 ? (
									IPRows.map((rows, index) => (
										<tr key={index}>
											{rows &&
												rows.length > 0 &&
												rows.map((row, rowIndex) => (
													<td key={row + rowIndex}>{row}</td>
												))}
											{userRole === 'admin' && (
												<td>
													<TableButton
														label='Delete'
														icon='trash'
														onClick={() =>
															setDeleteDeniedListModalStatus({ status: true, type: 'IP Address', data: rows })}
													/>
												</td>
											)}
										</tr>
									))
								) : (
									<tr>
										<td 
											colSpan={deniedData.IPSCols && deniedData.IPSCols.length} 
											style={{ textAlign: 'center' }}>
											No data found
										</td>
									</tr>
								)
							}
						/>

						<div className='paginationContainer'>
							<Pagination
								onPageChange={handleIPPageChange}
								rowsPerPage={pageLimit}
								totalPageSize={IPTotalRows}
								pageNumber={pageNumberIP}
							/>
						</div>
					</div>

					<div className={DeniedListsStyles.table}>
						<div className={DeniedListsStyles.table_heading}>
							<div className={DeniedListsStyles.table_header}>
								<h3 className={DeniedListsStyles.table_header_text}>Denied Services / Ports</h3>
								{userRole === 'admin' && (
									<button
										className={DeniedListsStyles.table_header_btn}
										onClick={() => setAddDeniedListModalStatus({ status: true, type: 'Services/Ports' })}>
										<svg className={DeniedListsStyles.table_header_btn_icon}>
											<use xlinkHref={`/assets/sprite.svg#icon-plus`} />
										</svg>
									</button>
								)}
							</div>
							<TextInput
								icon='search'
								placeholder='Search Here'
								value={searchPortKey}
								onChange={(e) => setSearchPortKey(e.target.value)}
								style={{ width: '15rem' }}
							/>
						</div>
						<Table
							containerStyle={{ height: '70vh' }}
							tableHead={
								<tr>
									{deniedData.PortCols &&
										deniedData.PortCols.length > 0 &&
										deniedData.PortCols.map((col, index) => <th key={col + index}>{col}</th>)}
								</tr>
							}
							tableRow={
								PortTotalRows > 0 && PortRows.length > 0 ? (
									PortRows.map((rows, index) => (
										<tr key={index}>
											{rows &&
												rows.length > 0 &&
												rows.map((row, rowIndex) => (
													<td key={row + rowIndex}>{row}</td>
												))}
											{userRole === 'admin' && (
												<td>
													<TableButton
														label='Delete'
														icon='trash'
														onClick={() =>
															setDeleteDeniedListModalStatus({ status: true, type: 'Services/Ports', data: rows })}
													/>
												</td>
											)}
										</tr>
									))
								) : (
									<tr>
										<td 
											colSpan={deniedData.PortCols && deniedData.PortCols.length} 
											style={{ textAlign: 'center' }}>
											No data found
										</td>
									</tr>
								)
							}
						/>

						<div className='paginationContainer'>
							<Pagination
								onPageChange={handlePortPageChange}
								rowsPerPage={pageLimit}
								totalPageSize={PortTotalRows}
								pageNumber={pageNumberPort}
							/>
						</div>
					</div>

					<div className={DeniedListsStyles.table}>
						<div className={DeniedListsStyles.table_heading}>
							<div className={DeniedListsStyles.table_header}>
								<h3 className={DeniedListsStyles.table_header_text}>Watched File Hashes</h3>
								{userRole === 'admin' && (
									<button
										className={DeniedListsStyles.table_header_btn}
										onClick={() => setAddDeniedListModalStatus({ status: true, type: 'Hash' })}>
										<svg className={DeniedListsStyles.table_header_btn_icon}>
											<use xlinkHref={`/assets/sprite.svg#icon-plus`} />
										</svg>
									</button>
								)}
							</div>
							<TextInput
								icon='search'
								placeholder='Search Here'
								value={searchHashKey}
								onChange={(e) => setSearchHashKey(e.target.value)}
								style={{ width: '15rem' }}
							/>
						</div>
						<Table
							containerStyle={{ height: '70vh' }}
							tableHead={
								<tr>
									{deniedData.HashCols &&
										deniedData.HashCols.length > 0 &&
										deniedData.HashCols.map((col, index) => <th key={col + index}>{col}</th>)}
								</tr>
							}
							tableRow={
								HashTotalRows > 0 && HashRows.length > 0 ? (
									HashRows.map((rows, index) => (
										<tr key={index}>
											{rows &&
												rows.length > 0 &&
												rows.map((row, rowIndex) => (
													<td key={row + rowIndex}>{row}</td>
												))}
											{userRole === 'admin' && (
												<td>
													<TableButton
														label='Delete'
													icon='trash'
													onClick={() =>
														setDeleteDeniedListModalStatus({ status: true, type: 'Hash', data: rows })}
													/>
												</td>
											)}
										</tr>
									))
								) : (
									<tr>
										<td
											colSpan={deniedData.HashCols && deniedData.HashCols.length}
											style={{ textAlign: 'center' }}>
											No data found
										</td>
									</tr>
								)
							}
						/>

						<div className='paginationContainer'>
							<Pagination
								onPageChange={handleHashPageChange}
								rowsPerPage={pageLimit}
								totalPageSize={HashTotalRows}
								pageNumber={pageNumberHash}
							/>
						</div>
					</div>
				</div>
			)}

			<AddDeniedModal
				data={{
					addDeniedListModalStatus,
					newDeniedText,
					setNewDeniedText,
					newDeniedFile,
					setNewDeniedFile,
					closeModal,
					handleAddDenied
				}}
			/>
			<DeleteDeniedModal data={{ deleteDeniedListModalStatus, closeDeleteModal, handleDeleteDenied }} />
		</Content>
	);
};

export default DeniedListsScreen;
